import React, { useState } from 'react';
import { MessageSquare, FileText, X, Sparkles } from 'lucide-react';
import ChatPanel from './ChatPanel';
import InfiniteCanvasModal from './InfiniteCanvasModal';

interface ScratchpadItem {
  id: number;
  type: string;
  content: string;
  source: string;
  category?: string;
}

type StepStatusItem = {
  uid?: string;
  seq?: number;
  id?: number;
  title?: string;
  status?: string;
};

interface RightPanelProps {
  sessionId?: string | null;
  agentId?: string | null;
  scratchpadItems: ScratchpadItem[];
  onRemoveScratchpadItem: (id: number) => void;
  onChatMessage?: (message: string, options?: { onToken?: (chunk: string) => void }) => Promise<any>;
  onGenerateFraming?: (goal: string) => Promise<any>;
  isBusy?: boolean;
  width?: number; // pixels, controlled by parent for resize
  categories?: string[];
  onAddCategory?: (name: string) => void;
  onUpdateScratchpadItem?: (id: number, content: string) => void;
  onAddScratchpadItem?: (category: string, content: string) => void;
  stepStatuses?: StepStatusItem[];
  stepStage?: string | null;
  onCanvasCategoriesChange?: (names: string[]) => void;
  onCloseWorkspace?: () => void;
  forceActiveTab?: 'chat' | 'scratch' | null; // Allow parent to control active tab
  isClosingWorkspace?: boolean;
}

const RightPanel: React.FC<RightPanelProps> = ({
  sessionId,
  agentId,
  scratchpadItems,
  onRemoveScratchpadItem,
  onChatMessage,
  onGenerateFraming,
  isBusy = false,
  width = 384,
  categories = ["Ungrouped"],
  onAddCategory,
  onUpdateScratchpadItem,
  onAddScratchpadItem,
  stepStatuses = [],
  stepStage = null,
  onCanvasCategoriesChange,
  onCloseWorkspace,
  forceActiveTab = null,
  isClosingWorkspace = false,
}) => {
  // Default to 'chat' tab at launch (not scratchpad)
  const [activeTab, setActiveTab] = useState<'chat' | 'scratch'>('chat');
  
  // Update tab when forceActiveTab changes (only when explicitly set by parent)
  React.useEffect(() => {
    if (forceActiveTab) {
      setActiveTab(forceActiveTab);
      return;
    }
    setActiveTab('chat');
  }, [forceActiveTab]);
  const [showCanvas, setShowCanvas] = useState(false);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    // Drag and drop functionality is handled by parent component
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  // Stage label now handled within ChatPanel

  // Preserve formatting on paste into contentEditable scratchpad items
  const sanitizeHtml = (html: string) => {
    const template = document.createElement('template');
    template.innerHTML = html;
    const allowedTags = new Set([
      'b','strong','i','em','u','a','p','br','ul','ol','li','h1','h2','h3','code','pre','span','div'
    ]);
    const allowedAttrs = new Set(['href','target','rel']);
    const walk = (node: Node) => {
      if (node.nodeType === 1) {
        const el = node as Element;
        const tag = el.tagName.toLowerCase();
        if (!allowedTags.has(tag)) {
          const parent = el.parentNode;
          while (el.firstChild) parent?.insertBefore(el.firstChild, el);
          parent?.removeChild(el);
          return;
        }
        // Strip disallowed attributes
        Array.from(el.attributes).forEach((attr) => {
          if (!allowedAttrs.has(attr.name.toLowerCase())) {
            el.removeAttribute(attr.name);
          }
        });
        // Ensure links are safe
        if (tag === 'a') {
          el.setAttribute('target', '_blank');
          el.setAttribute('rel', 'noreferrer');
        }
      }
      Array.from(node.childNodes).forEach(walk);
    };
    walk(template.content);
    return template.innerHTML;
  };

  const handleScratchpadPaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    const html = e.clipboardData.getData('text/html');
    const text = e.clipboardData.getData('text/plain');
    if (!html && !text) return;
    e.preventDefault();
    const toInsert = html ? sanitizeHtml(html) : text.replace(/\n/g, '<br>');
    // Insert sanitized HTML at caret
    document.execCommand('insertHTML', false, toInsert);
  };

  return (
    <div
      className="bg-gradient-to-b from-indigo-900/40 to-purple-950/40 border-l border-indigo-800/30 flex flex-col"
      style={{ width: `${width}px` }}
    >
      {/* Header with Tabs */}
      <div className="p-4 border-b border-indigo-800/30 right-panel-header">
        <div className="flex items-center justify-between gap-2 mb-3">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-indigo-400" />
            <h2 className="text-sm font-semibold text-white">astra agent</h2>
          </div>
          {onCloseWorkspace && (
            <button
              type="button"
              onClick={onCloseWorkspace}
              disabled={isClosingWorkspace}
              className="transition-all w-7 h-7 p-0 rounded-lg border right-panel-close-btn flex items-center justify-center shrink-0"
              style={{
                color: isClosingWorkspace ? '#9ca3af' : '#e2e8f0',
                borderColor: isClosingWorkspace ? 'rgba(75, 85, 99, 0.6)' : 'rgba(148, 163, 184, 0.36)',
                backgroundColor: isClosingWorkspace ? 'rgba(51, 65, 85, 0.2)' : 'rgba(255, 255, 255, 0.08)',
                backdropFilter: 'blur(10px) saturate(140%)',
                WebkitBackdropFilter: 'blur(10px) saturate(140%)',
                boxShadow: isClosingWorkspace
                  ? 'none'
                  : 'inset 0 1px 0 rgba(255,255,255,0.16), 0 6px 16px rgba(15,23,42,0.28)',
                cursor: isClosingWorkspace ? 'not-allowed' : 'pointer'
              }}
              onMouseEnter={(e) => {
                if (!isClosingWorkspace) {
                  e.currentTarget.style.color = '#eff6ff';
                  e.currentTarget.style.borderColor = 'rgba(96, 165, 250, 1)';
                  e.currentTarget.style.backgroundColor = 'rgba(37, 99, 235, 0.55)';
                  e.currentTarget.style.boxShadow = '0 0 0 1px rgba(147, 197, 253, 0.45), 0 8px 22px rgba(37, 99, 235, 0.45)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isClosingWorkspace) {
                  e.currentTarget.style.color = '#e2e8f0';
                  e.currentTarget.style.borderColor = 'rgba(148, 163, 184, 0.36)';
                  e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.08)';
                  e.currentTarget.style.boxShadow = 'inset 0 1px 0 rgba(255,255,255,0.16), 0 6px 16px rgba(15,23,42,0.28)';
                }
              }}
              aria-label="Close workspace"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
        
        <div className="flex gap-2">
          <button
            onClick={() => setActiveTab('chat')}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg transition-all ${
              activeTab === 'chat'
                ? 'tab-glass-active'
                : 'tab-glass'
            }`}
          >
            <MessageSquare className="w-4 h-4" />
            <span className="text-sm font-medium">Chat</span>
          </button>
          <button
            onClick={() => setActiveTab('scratch')}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg transition-all ${
              activeTab === 'scratch'
                ? 'tab-glass-active'
                : 'tab-glass'
            }`}
          >
            <FileText className="w-4 h-4" />
            <span className="text-sm font-medium">Analysis Scratchpad</span>
          </button>
        </div>
      </div>

      {/* Content Area */}
      <div className="flex-1 overflow-hidden flex flex-col">
        {activeTab === 'chat' ? (
          <>
            <ChatPanel
              sessionId={sessionId}
              agentId={agentId}
              onChatMessage={onChatMessage}
              isBusy={isBusy}
            />
          </>
        ) : (
          <div className="flex-1 flex flex-col">
            {/* Scratchpad actions */}
            <div className="px-4 pt-3 flex justify-end">
              <button
                type="button"
                className="bg-indigo-500 hover:bg-indigo-600 text-white text-xs font-semibold rounded-lg px-3 py-2 shadow"
                onClick={() => setShowCanvas(true)}
                title="Open infinite canvas"
              >
                Open Canvas
              </button>
            </div>
          <div
            className="flex-1 overflow-y-auto p-4"
            onDrop={handleDrop}
            onDragOver={handleDragOver}
          >
            {categories.length === 0 ? (
                <div className="text-indigo-300 text-[13px]">No categories yet.</div>
            ) : (
              <div className="space-y-4">
                {categories.map((cat) => {
                  const items = scratchpadItems.filter(i => (i.category || 'Ungrouped') === cat);
                  if (items.length === 0) return null;
                  return (
                      <div key={cat} className="scratchpad-glass rounded p-2">
                        <div className="px-1 py-1 text-indigo-300 text-[11px] font-semibold uppercase tracking-wide mb-1 text-left">{cat}</div>
                        <div className="space-y-2">
                        {items.map((item) => (
                            <div key={item.id} className="flex items-start gap-2">
                              <div className="scratchpad-bullet mt-2" />
                              <div className="relative group flex-1 p-1">
                            <button
                              onClick={() => onRemoveScratchpadItem(item.id)}
                              className="absolute top-2 right-2 text-white p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                              style={{ backgroundColor: 'rgba(239, 68, 68, 0.8)' }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = 'rgba(220, 38, 38, 1)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'rgba(239, 68, 68, 0.8)';
                              }}
                            >
                              <X className="w-3 h-3" />
                            </button>
                            {item.type === 'text' && (
                                  <div
                                    className="scratchpad-compact text-indigo-100 outline-none"
                                    contentEditable
                                    suppressContentEditableWarning
                                    onPaste={handleScratchpadPaste}
                                    dangerouslySetInnerHTML={{ __html: item.content || '' }}
                                    onBlur={(e) => onUpdateScratchpadItem && onUpdateScratchpadItem(item.id, (e.target as HTMLDivElement).innerHTML)}
                                  />
                            )}
                            {item.type === 'image' && (
                              <img src={item.content} alt="Scratchpad item" className="w-full rounded" />
                            )}
                              </div>
                          </div>
                        ))}
                          {/* Minimal add-bullet control */}
                          <form
                            className="mt-1"
                            onSubmit={(e) => {
                              e.preventDefault();
                              const input = e.currentTarget.elements.namedItem(`bullet-${cat}`) as HTMLInputElement | null;
                              const value = (input?.value || '').trim();
                              if (!value) return;
                              onAddScratchpadItem && onAddScratchpadItem(cat, value);
                              if (input) input.value = '';
                            }}
                          >
                            <div className="flex items-start gap-2">
                              <div className="scratchpad-bullet mt-2" />
                              <div className="relative flex-1">
                                <input
                                  name={`bullet-${cat}`}
                                  placeholder="Add bullet"
                                  className="scratchpad-input w-full pr-6"
                                />
                                <button type="submit" className="enter-glyph" aria-label="Add bullet">↵</button>
                              </div>
                            </div>
                          </form>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            </div>
            <div className="border-t border-indigo-800/30 p-3">
              <div className="flex gap-2 items-center">
                <input
                  type="text"
                  placeholder="New category"
                  className="flex-1 bg-white text-gray-900 placeholder-slate-500 border border-indigo-700/50 rounded px-2 py-1 text-[13px]"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      const v = (e.target as HTMLInputElement).value;
                      onAddCategory && onAddCategory(v);
                      (e.target as HTMLInputElement).value = '';
                    }
                  }}
                />
                <span className="text-xs text-indigo-300">Press Enter to add</span>
              </div>
            </div>
          </div>
        )}
      </div>
      {/* Canvas modal */}
      <InfiniteCanvasModal
        isOpen={showCanvas}
        onClose={() => setShowCanvas(false)}
        items={scratchpadItems.map(i => ({ id: i.id, type: i.type, content: i.content, category: i.category }))}
        onAddCategory={onAddCategory}
        onCategoriesChange={onCanvasCategoriesChange}
      />
    </div>
  );
};

export default React.memo(RightPanel);

